import type { MouseEventHandler } from "react";

import { appStoreUrl, playStoreUrl, type StoreLinkSurface } from "@/lib/storeLinks";
import { trackMarketingCtaClick, type MarketingCtaPlacement } from "@/services/marketingCtaAnalytics.service";

interface StoreBadgesProps {
  className?: string;
  /** Which page/area the badges live on; drives href attribution and the click event's surface param. */
  surface: StoreLinkSurface;
  placement?: MarketingCtaPlacement;
  trackingId?: string;
  /** Badge order; lead with Google Play for visitors known to be on Android. */
  order?: "app-store-first" | "play-store-first";
  onAppStoreClick?: MouseEventHandler<HTMLAnchorElement>;
  onPlayStoreClick?: MouseEventHandler<HTMLAnchorElement>;
}

/** App Store + Google Play download badges, used across marketing pages. */
export const StoreBadges = ({
  className = "h-12 md:h-14",
  surface,
  placement = "body",
  trackingId,
  order = "app-store-first",
  onAppStoreClick,
  onPlayStoreClick,
}: StoreBadgesProps) => {
  const appStoreHref = appStoreUrl(surface);
  const playStoreHref = playStoreUrl(surface);

  const handleAppStoreClick: MouseEventHandler<HTMLAnchorElement> = event => {
    trackMarketingCtaClick({
      destination: "app_store",
      surface,
      placement,
      label: "Download on the App Store",
      href: appStoreHref,
      ctaId: trackingId,
    });
    onAppStoreClick?.(event);
  };

  const handlePlayStoreClick: MouseEventHandler<HTMLAnchorElement> = event => {
    trackMarketingCtaClick({
      destination: "play_store",
      surface,
      placement,
      label: "Get it on Google Play",
      href: playStoreHref,
      ctaId: trackingId,
    });
    onPlayStoreClick?.(event);
  };

  const appStoreBadge = (
    <a
      key="app-store"
      href={appStoreHref}
      target="_blank"
      rel="noopener noreferrer"
      data-marketing-cta-tracked="true"
      className="transition"
      onClick={handleAppStoreClick}
    >
      <img alt="Download on the App Store" src="/Download_on_App_Store.svg" className={`${className} w-auto`} />
    </a>
  );

  const playStoreBadge = (
    <a
      key="play-store"
      href={playStoreHref}
      target="_blank"
      rel="noopener noreferrer"
      data-marketing-cta-tracked="true"
      className="transition"
      onClick={handlePlayStoreClick}
    >
      <img alt="Get it on Google Play" src="/Google_Play_Store_badge_EN.svg" className={`${className} w-auto`} />
    </a>
  );

  return <>{order === "play-store-first" ? [playStoreBadge, appStoreBadge] : [appStoreBadge, playStoreBadge]}</>;
};
